import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  CpuChipIcon, 
  ClockIcon, 
  ChartBarIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  SignalIcon,
  ServerIcon,
  CircleStackIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { healthCheck } from '../services/api';

export default function SystemHealthMonitor() {
  const [performanceMetrics, setPerformanceMetrics] = useState({
    apiResponseTime: 0,
    memoryUsage: 0,
    cpuUsage: 0,
    activeConnections: 0,
    queueSize: 0,
    errors24h: 0
  });

  const { data: healthData, isError: healthError, refetch } = useQuery({
    queryKey: ['system-health'],
    queryFn: healthCheck,
    refetchInterval: 10000, // Check every 10 seconds
    retry: 3,
  });

  // Simulate real-time performance metrics (in a real app, this would come from APIs)
  useEffect(() => {
    const updateMetrics = () => {
      setPerformanceMetrics(prev => ({
        apiResponseTime: Math.random() * 1000 + 100, // 100-1100ms
        memoryUsage: Math.random() * 30 + 40, // 40-70%
        cpuUsage: Math.random() * 40 + 20, // 20-60%
        activeConnections: Math.floor(Math.random() * 50) + 10,
        queueSize: Math.floor(Math.random() * 20),
        errors24h: Math.floor(Math.random() * 5)
      }));
    };

    updateMetrics();
    const interval = setInterval(updateMetrics, 5000); // Update every 5 seconds
    return () => clearInterval(interval);
  }, []);

  const getHealthStatus = () => {
    if (healthError) return 'critical';
    if (!healthData) return 'unknown';
    
    const { apiResponseTime, memoryUsage, cpuUsage, errors24h } = performanceMetrics;
    
    if (apiResponseTime > 800 || memoryUsage > 85 || cpuUsage > 80 || errors24h > 10) {
      return 'warning';
    }
    if (apiResponseTime > 500 || memoryUsage > 70 || cpuUsage > 60 || errors24h > 5) {
      return 'warning';
    }
    return 'healthy';
  };

  const healthStatus = getHealthStatus();

  const statusConfig = {
    healthy: {
      color: 'text-green-600 bg-green-50 border-green-200',
      iconColor: 'text-green-500',
      icon: CheckCircleIcon,
      label: 'Healthy'
    },
    warning: {
      color: 'text-yellow-600 bg-yellow-50 border-yellow-200',
      iconColor: 'text-yellow-500',
      icon: ExclamationTriangleIcon,
      label: 'Warning'
    },
    critical: {
      color: 'text-red-600 bg-red-50 border-red-200',
      iconColor: 'text-red-500',
      icon: ExclamationTriangleIcon,
      label: 'Critical'
    },
    unknown: {
      color: 'text-gray-600 bg-gray-50 border-gray-200',
      iconColor: 'text-gray-500',
      icon: ClockIcon,
      label: 'Unknown'
    }
  };

  const currentStatus = statusConfig[healthStatus];

  const metricCards = [
    {
      label: 'API Response',
      value: `${performanceMetrics.apiResponseTime.toFixed(0)}ms`,
      icon: SignalIcon,
      status: performanceMetrics.apiResponseTime > 800 ? 'critical' : 
              performanceMetrics.apiResponseTime > 500 ? 'warning' : 'healthy',
      trend: Math.random() > 0.5 ? 'up' : 'down'
    },
    {
      label: 'Memory Usage',
      value: `${performanceMetrics.memoryUsage.toFixed(1)}%`,
      icon: CpuChipIcon,
      status: performanceMetrics.memoryUsage > 85 ? 'critical' : 
              performanceMetrics.memoryUsage > 70 ? 'warning' : 'healthy',
      trend: Math.random() > 0.5 ? 'up' : 'down'
    },
    {
      label: 'CPU Usage',
      value: `${performanceMetrics.cpuUsage.toFixed(1)}%`,
      icon: ServerIcon,
      status: performanceMetrics.cpuUsage > 80 ? 'critical' : 
              performanceMetrics.cpuUsage > 60 ? 'warning' : 'healthy',
      trend: Math.random() > 0.5 ? 'up' : 'down'
    },
    {
      label: 'Active Users',
      value: performanceMetrics.activeConnections.toString(),
      icon: CircleStackIcon,
      status: 'healthy',
      trend: Math.random() > 0.5 ? 'up' : 'down'
    },
    {
      label: 'Queue Size',
      value: performanceMetrics.queueSize.toString(),
      icon: ChartBarIcon,
      status: performanceMetrics.queueSize > 15 ? 'warning' : 'healthy',
      trend: Math.random() > 0.5 ? 'up' : 'down'
    },
    {
      label: 'Errors (24h)',
      value: performanceMetrics.errors24h.toString(),
      icon: ExclamationTriangleIcon,
      status: performanceMetrics.errors24h > 10 ? 'critical' : 
              performanceMetrics.errors24h > 5 ? 'warning' : 'healthy',
      trend: Math.random() > 0.5 ? 'up' : 'down'
    }
  ];

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <ServerIcon className="w-5 h-5 text-blue-500 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900">System Health</h2>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => refetch()}
            className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
            title="Refresh health data"
          >
            <ArrowPathIcon className="w-4 h-4" />
          </button>
          <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${currentStatus.color}`}>
            <currentStatus.icon className={`w-4 h-4 mr-1.5 ${currentStatus.iconColor}`} />
            {currentStatus.label}
          </div>
        </div>
      </div>

      {/* Overall Status */}
      <div className={`border rounded-lg p-4 mb-6 ${currentStatus.color}`}>
        <div className="flex items-center">
          <currentStatus.icon className={`w-6 h-6 ${currentStatus.iconColor} mr-3`} />
          <div>
            <h3 className="text-sm font-medium text-gray-900">
              System Status: {currentStatus.label}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {healthStatus === 'healthy' && 'All systems operational. Performance within normal parameters.'}
              {healthStatus === 'warning' && 'Some performance metrics are elevated. Monitoring closely.'}
              {healthStatus === 'critical' && 'Critical issues detected. Immediate attention required.'}
              {healthStatus === 'unknown' && 'Unable to determine system status. Checking connectivity...'}
            </p>
          </div>
        </div>
      </div>

      {/* Performance Metrics Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {metricCards.map((metric, index) => (
          <div key={index} className="border border-gray-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <metric.icon className={`w-4 h-4 mr-2 ${
                  metric.status === 'healthy' ? 'text-green-500' :
                  metric.status === 'warning' ? 'text-yellow-500' :
                  'text-red-500'
                }`} />
                <span className="text-xs font-medium text-gray-700">
                  {metric.label}
                </span>
              </div>
              <div className={`w-2 h-2 rounded-full ${
                metric.status === 'healthy' ? 'bg-green-400' :
                metric.status === 'warning' ? 'bg-yellow-400' :
                'bg-red-400'
              }`}></div>
            </div>
            <div className="mt-2 flex items-baseline justify-between">
              <span className="text-lg font-semibold text-gray-900">
                {metric.value}
              </span>
              <span className={`text-xs ${
                metric.trend === 'up' ? 'text-red-500' : 'text-green-500'
              }`}>
                {metric.trend === 'up' ? '↗' : '↘'}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Service Status */}
      <div>
        <h3 className="text-sm font-medium text-gray-900 mb-3">Service Components</h3>
        <div className="space-y-2">
          {[
            { name: 'Classification API', status: 'healthy', uptime: '99.9%' },
            { name: 'SCG Retrieval', status: 'healthy', uptime: '99.8%' },
            { name: 'Document Processing', status: 'healthy', uptime: '99.7%' },
            { name: 'Vector Database', status: healthError ? 'warning' : 'healthy', uptime: '99.5%' },
            { name: 'Authentication', status: 'healthy', uptime: '100%' }
          ].map((service, index) => (
            <div key={index} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-md">
              <div className="flex items-center">
                <div className={`w-2 h-2 rounded-full mr-3 ${
                  service.status === 'healthy' ? 'bg-green-400' :
                  service.status === 'warning' ? 'bg-yellow-400' :
                  'bg-red-400'
                }`}></div>
                <span className="text-sm font-medium text-gray-700">
                  {service.name}
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-xs text-gray-500">
                  {service.uptime} uptime
                </span>
                <span className={`text-xs font-medium ${
                  service.status === 'healthy' ? 'text-green-600' :
                  service.status === 'warning' ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {service.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Last Updated */}
      <div className="mt-4 pt-4 border-t border-gray-200 text-center">
        <p className="text-xs text-gray-500">
          Last updated: {new Date().toLocaleTimeString()} • Auto-refresh every 10s
        </p>
      </div>
    </div>
  );
}